import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const scenarios = [
  {
    id: 'calendar', label: 'Calendar', subtitle: 'Resolution engine', icon: FiIcons.FiCalendar,
    alertTag: 'Calendar Conflict', alertColor: 'text-[#8A6A2A]', alertBg: 'bg-[#FBF4E8]', alertBorder: 'border-[#DFC98A]',
    initialMessage: "Heads up — your 2PM meeting with Sarah overlaps with your protected Deep Work block tomorrow. Want me to move Sarah to your open slot on Thursday at 10AM?",
    actions: [
      { label: 'Yeah, move to Thursday', reply: "Done. I've moved Sarah to Thursday at 10AM and sent her a polite update.", followUp: { message: "I also noticed you usually send her a pre-read before these syncs. Want me to draft that based on yesterday's strategy notes?", actions: [{ label: 'Yes, draft it', result: "Drafted and pinned to your Priority Inbox. Your Deep Work block is fully intact." }, { label: 'Not this time', result: "Understood. The meeting is moved and your Deep Work block remains protected." }] } },
      { label: 'Keep it, override block', reply: "Understood. Maintaining the 2PM with Sarah.", followUp: { message: "Since we lost that focus time, should I block off 3:30PM to 5:00PM instead so you still get your Deep Work in?", actions: [{ label: 'Yes, block it', result: "Done. 3:30PM – 5:00PM is now heavily protected on your calendar." }, { label: 'No, leave it open', result: "Got it. The afternoon is left open for ad-hoc requests." }] } }
    ]
  },
  {
    id: 'email', label: 'Inbox', subtitle: 'Triage system', icon: FiIcons.FiMail,
    alertTag: 'High Priority Email', alertColor: 'text-[#8A2A2A]', alertBg: 'bg-[#FBF0EE]', alertBorder: 'border-[#DFA8A8]',
    initialMessage: "David from Acme Corp is asking for the revised Q3 proposal. I've already drafted a response in your voice and attached the latest PDF.",
    actions: [
      { label: 'Approve & Send', reply: "Sent. I've also added a follow-up reminder for next Tuesday in case he hasn't responded.", followUp: { message: "By the way, of the 14 other emails sorted, one is an overdue invoice from AWS. Do you want me to route that directly to accounting?", actions: [{ label: 'Yes, route it', result: "Routed to Accounting with a note to process immediately. Your inbox is clear." }, { label: 'I will handle it', result: "Pinned the AWS invoice to the top of your review queue. The rest are archived." }] } },
      { label: 'I need to edit it', reply: "Pinned the draft to the top of your Priority Inbox.", followUp: { message: "Should I hold any other incoming emails from David until you've had a chance to send this?", actions: [{ label: 'Yes, pause his threads', result: "Done. Any new emails from David will bypass your main inbox and queue quietly until the proposal is sent." }, { label: 'No, let them through', result: "Understood. Standard routing rules still apply." }] } }
    ]
  },
  {
    id: 'prep', label: 'Meetings', subtitle: 'Context assembly', icon: FiIcons.FiUsers,
    alertTag: 'Meeting Prep Ready', alertColor: 'text-[#2A6B4A]', alertBg: 'bg-[#EBF5F0]', alertBorder: 'border-[#8ACAAA]',
    initialMessage: "Your kickoff with the Delta team is in 15 minutes. I've compiled their recent support tickets and 4 suggested talking points based on their open questions.",
    actions: [
      { label: 'Send to my phone', reply: "Sent via Telegram. Bullet points and the PDF brief are ready.", followUp: { message: "I noticed Delta's usage dropped 15% this month. Want me to highlight the downgrade risk in your brief?", actions: [{ label: 'Yes, highlight it', result: "Updated the brief with a churn risk analysis section." }, { label: 'No, keep it positive', result: "Understood. Brief remains focused on kickoff objectives." }] } },
      { label: 'Add to calendar invite', reply: "Done. Added as private notes to the event — only visible to you.", followUp: { message: "Should I also draft a post-meeting summary template for you to fill in right after?", actions: [{ label: 'Yes, draft it', result: "Template created and linked in the calendar notes." }, { label: 'No need', result: "Got it. Context is loaded in the invite. Have a great kickoff." }] } }
    ]
  },
  {
    id: 'followups', label: 'Follow-ups', subtitle: 'Action tracker', icon: FiIcons.FiCheckSquare,
    alertTag: 'Action Items Detected', alertColor: 'text-[#2A4A8A]', alertBg: 'bg-[#EBF0FB]', alertBorder: 'border-[#8AAAD8]',
    initialMessage: "From your call with Marcus on Tuesday, I flagged 3 open commitments: a revised SOW, an intro to legal, and a pilot pricing follow-up. Want me to draft the outreach?",
    actions: [
      { label: 'Draft all three', reply: "Drafted. The SOW, intro, and pricing follow-up are staggered to send tomorrow morning.", followUp: { message: "Marcus also mentioned planning a Q4 offsite. Want me to research venue options?", actions: [{ label: 'Yes, standard hotels', result: "4 premium hotel options added to a new 'Q4 Offsite' Notion doc." }, { label: 'No, too early', result: "Understood. The staggered follow-ups are queued." }] } },
      { label: 'Just the SOW for now', reply: "SOW draft is in your Priority Inbox.", followUp: { message: "Should I auto-send the intro and pricing follow-up if he signs the SOW?", actions: [{ label: 'Yes, auto-send on sign', result: "Workflow created. Signature triggers the remaining outreach automatically." }, { label: 'No, ask me first', result: "Got it. I'll prompt you again on Thursday morning." }] } }
    ]
  },
  {
    id: 'research', label: 'Research', subtitle: 'Intel briefing', icon: FiIcons.FiSearch,
    alertTag: 'Pre-Call Brief Ready', alertColor: 'text-[#2A5C8A]', alertBg: 'bg-[#EBF2F9]', alertBorder: 'border-[#8ABDD9]',
    initialMessage: "You have a discovery call with Northgate Capital tomorrow. I've pulled their recent LinkedIn activity, a Series B news mention, and two mutual connections.",
    actions: [
      { label: 'Just the key points', reply: "Three-bullet summary sent to Telegram: funding stage, pain signals, and the mutual connection.", followUp: { message: "Should I also pull their recent engineering job listings? It might signal a tech stack rebuild.", actions: [{ label: 'Yes, that is useful', result: "Stack signals (moving to React/Node) appended to your Telegram thread." }, { label: 'No, focus on biz', result: "Understood. Telegram brief is final and ready for your review." }] } },
      { label: 'Send full brief', reply: "Full brief sent to your email with all sources linked.", followUp: { message: "One mutual connection is Sarah Jenkins. Want me to draft a quick message asking for background intel?", actions: [{ label: 'Draft it', result: "Drafted in WhatsApp. Just hit send when ready." }, { label: 'I will do it myself', result: "Got it. Full brief is sitting in your inbox for tomorrow." }] } }
    ]
  }
];

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3 bg-[#F0EDE8] border border-[#E4DFD8] rounded-2xl rounded-tl-sm w-fit h-10">
    {[0, 0.2, 0.4].map((delay, i) => (
      <motion.div key={i} animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay }}
        className="w-1.5 h-1.5 bg-[#9E9690] rounded-full" />
    ))}
  </div>
);

const ScenarioTab = ({ scenario, isActive, onClick }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 text-left ${isActive ? 'bg-[#EBF2F9] border-[#B8CDD9]' : 'bg-white border-[#E4DFD8] hover:bg-[#F7F5F2] hover:border-[#D4CFC8]'}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-[#2A5C8A] text-white' : 'bg-[#F0EDE8] text-[#6B6560]'}`}>
      <SafeIcon icon={scenario.icon} className="text-sm" />
    </div>
    <div className="flex-grow min-w-0">
      <h4 className={`text-sm font-semibold ${isActive ? 'text-[#1E4D7A]' : 'text-[#1A1A18]'}`}>{scenario.label}</h4>
      <p className="text-[10px] text-[#9E9690] font-mono-custom mt-0.5">{scenario.subtitle}</p>
    </div>
    {isActive && <div className="w-1 h-6 bg-[#2A5C8A] rounded-full flex-shrink-0" />}
  </button>
);

const HowItWorks = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const chatEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const activeScenario = scenarios[activeIdx];

  useEffect(() => {
    setMessages([{ role: 'assistant', content: activeScenario.initialMessage, type: 'initial' }]);
    setActiveStep({ actions: activeScenario.actions });
    setIsTyping(false);
  }, [activeIdx]);

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, isTyping, activeStep]);

  const handleActionClick = (action) => {
    setMessages(prev => [...prev, { role: 'user', content: action.label }]);
    setActiveStep(null);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      if (action.followUp) {
        setMessages(prev => [...prev,
          { role: 'assistant', content: action.reply, type: 'text' },
          { role: 'assistant', content: action.followUp.message, type: 'followup' }
        ]);
        setActiveStep({ actions: action.followUp.actions });
      } else if (action.result) {
        setMessages(prev => [...prev, { role: 'assistant', content: action.result, type: 'success' }]);
      }
    }, 1100);
  };

  return (
    <section id="how-it-works" className="py-32 bg-[#F7F5F2] relative">
      {/* Notebook grid */}
      <div className="absolute inset-0 notebook-bg pointer-events-none" />
      <div className="absolute top-0 bottom-0 left-[88px] w-px bg-[rgba(220,80,80,0.15)] hidden lg:block" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="tag-pill mb-5">Interactive Demo</div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-5 leading-tight tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              See the autonomy<br /><span className="gradient-text-accent">in action.</span>
            </h2>
            <p className="text-base text-[#6B6560] mb-8 leading-relaxed">
              WeekOne doesn't wait for commands — it anticipates the next logical step. Pick a scenario below.
            </p>
            <div className="space-y-2">
              {scenarios.map((s, idx) => (
                <ScenarioTab key={s.id} scenario={s} isActive={activeIdx === idx} onClick={() => setActiveIdx(idx)} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 lg:sticky lg:top-28">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white border border-[#E4DFD8] rounded-2xl overflow-hidden shadow-md flex flex-col"
              style={{ maxHeight: '600px' }}>
              <div className="px-5 py-4 border-b border-[#EDE9E4] bg-white flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-9 h-9 rounded-xl bg-[#1A1A18] flex items-center justify-center">
                      <SafeIcon icon={FiIcons.FiCpu} className="text-[#F7F5F2] text-base" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#3D7A5E] border-2 border-white rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#1A1A18]">WeekOne Assistant</h3>
                    <p className="text-[10px] text-[#9E9690] font-mono-custom">Active · Connected</p>
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border font-mono-custom ${activeScenario.alertBg} ${activeScenario.alertColor} ${activeScenario.alertBorder}`}>
                  <SafeIcon icon={activeScenario.icon} className="text-[10px]" />
                  {activeScenario.label}
                </div>
              </div>

              <div ref={chatBodyRef} className="p-5 bg-[#F7F5F2]/60 flex flex-col gap-4 overflow-y-auto flex-1"
                style={{ overscrollBehavior: 'contain' }}>
                <AnimatePresence mode="popLayout" initial={false}>
                  {messages.map((msg, idx) => (
                    <motion.div key={`msg-${idx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                      className={`flex items-start gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'assistant' && (
                        <div className="w-6 h-6 rounded-lg bg-[#1A1A18] flex items-center justify-center flex-shrink-0 mt-1">
                          <SafeIcon icon={FiIcons.FiCpu} className="text-[#F7F5F2] text-[9px]" />
                        </div>
                      )}
                      <div className={`max-w-[85%] p-3.5 text-sm leading-relaxed rounded-2xl ${
                        msg.role === 'user' ? 'bg-[#1A1A18] text-[#F7F5F2] rounded-tr-sm'
                        : msg.type === 'success' ? 'bg-[#EBF5F0] border border-[#8ACAAA] text-[#1A1A18] rounded-tl-sm'
                        : 'bg-white border border-[#E4DFD8] text-[#1A1A18] rounded-tl-sm shadow-sm'}`}>
                        {msg.type === 'initial' && (
                          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider mb-2.5 border font-mono-custom ${activeScenario.alertBg} ${activeScenario.alertColor} ${activeScenario.alertBorder}`}>
                            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-[9px]" />
                            {activeScenario.alertTag}
                          </div>
                        )}
                        {msg.type === 'success' && (
                          <div className="flex items-center gap-1.5 mb-2">
                            <SafeIcon icon={FiIcons.FiCheckCircle} className="text-[#3D7A5E] text-sm" />
                            <span className="text-[#2A6B4A] text-[9px] font-mono-custom uppercase tracking-wider font-semibold">Executed</span>
                          </div>
                        )}
                        <p>{msg.content}</p>
                      </div>
                    </motion.div>
                  ))}
                  {isTyping && (
                    <motion.div key="typing" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="flex items-start gap-2.5">
                      <div className="w-6 h-6 rounded-lg bg-[#1A1A18] flex items-center justify-center flex-shrink-0 mt-1">
                        <SafeIcon icon={FiIcons.FiCpu} className="text-[#F7F5F2] text-[9px]" />
                      </div>
                      <TypingIndicator />
                    </motion.div>
                  )}
                  {activeStep && !isTyping && (
                    <motion.div key={`actions-${messages.length}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className="flex flex-wrap gap-2 ml-8 mt-1">
                      {activeStep.actions.map((action, i) => (
                        <button key={i} onClick={() => handleActionClick(action)}
                          className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-all duration-200 border ${
                            i === 0 ? 'bg-[#1A1A18] text-[#F7F5F2] border-[#1A1A18] hover:bg-[#2A2A26]'
                            : 'bg-white text-[#6B6560] border-[#E4DFD8] hover:bg-[#F7F5F2] hover:border-[#D4CFC8]'}`}>
                          {action.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
                <div ref={chatEndRef} className="h-1" />
              </div>

              <div className="px-5 py-3.5 bg-white border-t border-[#EDE9E4] flex items-center gap-3 flex-shrink-0">
                <div className="flex-1 bg-[#F7F5F2] border border-[#E4DFD8] rounded-lg px-3.5 py-2 text-xs text-[#9E9690] font-mono-custom flex items-center justify-between">
                  <span>Assistant is waiting for commands...</span>
                  <SafeIcon icon={FiIcons.FiMic} className="text-[#C8C0B4]" />
                </div>
                <button className="w-8 h-8 rounded-lg bg-[#F0EDE8] text-[#9E9690] flex items-center justify-center border border-[#E4DFD8] pointer-events-none">
                  <SafeIcon icon={FiIcons.FiSend} className="text-xs" />
                </button>
              </div>
            </motion.div>
            <p className="text-center text-[10px] text-[#9E9690] font-mono-custom mt-3">
              * Simulated multi-step reasoning based on live WeekOne models.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;